import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WITHOUT_NOTIFICATION_HEADER } from '@core/constants';
import {
  IdempotencyKeyModel,
  PaymentCardModel,
  PaymentsCardResponseModel,
  SingleItemResponse,
  ThreeDSType,
  WithoutNotificationHeader,
} from '@core/models';
import { SessionState } from '@core/state/shared';
import { environment } from '@env/environment';
import { Observable, map, of, switchMap } from 'rxjs';
import { ThreeDSService } from '../3ds/3ds.service';
import { Api } from '../api.state';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentCardService extends Api<PaymentsCardResponseModel> {
  override endpoint: string;

  constructor(
    protected override readonly apiService: ApiService<PaymentsCardResponseModel>,
    private http: HttpClient,
    private sessionState: SessionState,
    private threeDSService: ThreeDSService
  ) {
    super(apiService);
    this.endpoint = `${environment.apiUrl}/v1/payments/payment-management/cards`;
  }

  public generateIdempotency(): Observable<IdempotencyKeyModel> {
    const url = `${environment.apiUrl}/v1/payments/generate-idempotency`;
    return this.http
      .get<SingleItemResponse<IdempotencyKeyModel>>(url)
      .pipe(map((response) => response.data));
  }

  public paymentCard(
    data: PaymentCardModel
  ): Observable<PaymentsCardResponseModel> {
    return this.post(data);
  }

  public multiplePayment(
    data: PaymentCardModel,
    idempotencyKey: string
  ): Observable<PaymentsCardResponseModel> {
    const headers = new HttpHeaders({
      'Idempotency-Key': idempotencyKey,
    });
    const endpoint = `${this.endpoint}/multiple`;
    const params = JSON.stringify(data);
    return this.http
      .post<SingleItemResponse<PaymentsCardResponseModel>>(endpoint, params, {
        headers,
      })
      .pipe(
        switchMap((response) => {
          if (response.data.iframe) {
            return this.threeDSService.open3DS(
              ThreeDSType.VALIDATING_PAYMENT,
              response.data,
              {
                baseUrl: `/v1/payments/sse?payment_id=${response.data.id}`,
                token: this.sessionState.accessToken,
              },
              (paymentId) => this.getMultiplePayment(paymentId)
            );
          }
          return of(response.data);
        })
      );
  }

  private getMultiplePayment(
    paymentId: string
  ): Observable<PaymentsCardResponseModel> {
    const endpoint = `${this.endpoint}/multiple/asynchronous/${paymentId}`;
    return this.http
      .get<SingleItemResponse<PaymentsCardResponseModel>>(endpoint, {
        headers: {
          [WITHOUT_NOTIFICATION_HEADER]: WithoutNotificationHeader.ALL,
        },
      })
      .pipe(map((response) => response.data));
  }
}
